import { createStore, useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { PreviousHandOff } from '../model/insertHandOff';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { getPlatformStorage } from '@/util/platformStorage';

/** A reply draft handed to a conversation's composer and not yet sent. */
export type HandOffLink = PreviousHandOff & {
  replyDraftId: string;
};

type HandOffState = {
  links: Record<string, HandOffLink>;
};

type HandOffActions = {
  record: (conversationId: string, link: HandOffLink) => void;
  clear: (conversationId: string) => void;
};

export type HandOffStore = HandOffState & HandOffActions;

/**
 * Which draft each conversation's composer text came from, so the send can
 * name it. The link lasts until a send succeeds. It is not dropped when the
 * composer empties: gifted-chat empties it just before handing a message to
 * the send, through the same callback as typing, so clearing there unlinked
 * every reply. An expert who deletes the text and writes their own is still
 * linked, and the server records it as a heavily edited draft, which is true.
 *
 * It is persisted beside the composer's own saved text: a link held only in
 * memory would be lost when the app is killed, and a full reply sent without
 * the draft's id makes the server discard the draft, which loses the pilot's
 * sent-as-drafted measure for that reply.
 *
 * `text` and `start` are where the hand-off landed, so a second hand-off can
 * replace it.
 */
export const createHandOffStore = () =>
  createStore<HandOffStore>()(
    persist(
      (set) => ({
        links: {},
        record: (conversationId, link) =>
          set((state) => ({ links: { ...state.links, [conversationId]: link } })),
        clear: (conversationId) =>
          set((state) => {
            if (!(conversationId in state.links)) {
              return state;
            }

            const { [conversationId]: _cleared, ...links } = state.links;

            return { links };
          }),
      }),
      {
        name: STORAGE_KEYS.REPLY_DRAFT_HAND_OFFS,
        storage: createJSONStorage(() => getPlatformStorage()),
        partialize: ({ links }) => ({ links }),
      }
    )
  );

export const handOffStore = createHandOffStore();

export function useHandOffLink(conversationId: string): HandOffLink | undefined {
  return useStore(handOffStore, (state) => state.links[conversationId]);
}

export function readHandOffLink(conversationId: string): HandOffLink | undefined {
  return handOffStore.getState().links[conversationId];
}

export function recordHandOff(conversationId: string, link: HandOffLink) {
  handOffStore.getState().record(conversationId, link);
}

export function clearHandOff(conversationId: string) {
  handOffStore.getState().clear(conversationId);
}
